import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const Footer = ({ onOpenContactForm }) => {
  const currentYear = new Date().getFullYear();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer data-testid="main-footer" className="bg-[#000000] text-slate-400 relative overflow-hidden">
      <div className="h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-md flex items-center justify-center">
                <span className="text-white font-black text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>T</span>
              </div>
              <span className="text-base tracking-tight text-white font-normal uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Trademark My LLC
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Licensed trademark attorneys fighting for your brand protection. US trademark registration, copyright filing, LLC formation, and Amazon Brand Registry enrollment.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-amber-500 font-bold text-xs uppercase tracking-[0.15em] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {['home', 'services', 'process', 'pricing', 'blog', 'testimonials', 'faq'].map(id => (
                <li key={id}>
                  <button data-testid={`footer-link-${id}`} onClick={() => scrollTo(id)} className="text-sm text-slate-400 hover:text-amber-400 transition-colors capitalize font-medium">
                    {id === 'blog' ? 'Resources' : id}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-amber-500 font-bold text-xs uppercase tracking-[0.15em] mb-6">Services</h4>
            <ul className="space-y-3">
              {['Trademark Registration', 'Copyright Registration', 'Company Formation', 'Trademark Search', 'Amazon Brand Registry'].map(service => (
                <li key={service}>
                  <button onClick={() => scrollTo('services')} className="text-sm text-slate-400 hover:text-amber-400 transition-colors text-left font-medium">{service}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-amber-500 font-bold text-xs uppercase tracking-[0.15em] mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs text-slate-500 mb-0.5">Phone</div>
                  <a href="tel:+18001234567" className="text-sm text-white hover:text-amber-400 transition-colors font-medium">+1 (800) 123-4567</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs text-slate-500 mb-0.5">Email</div>
                  <a href="mailto:info@trademarkmyllc.com" className="text-sm text-white hover:text-amber-400 transition-colors break-all font-medium">info@trademarkmyllc.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs text-slate-500 mb-0.5">Office</div>
                  <span className="text-sm text-white font-medium">3395 Michelson Dr Building 3, Irvine, CA 92612</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Watermark */}
        <div className="mb-12 overflow-hidden">
          <span className="text-[6vw] lg:text-[5vw] font-normal leading-none text-outline whitespace-nowrap select-none block uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Trademark My LLC
          </span>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-slate-500">&copy; {currentYear} Trademark My LLC. All rights reserved.</p>
          <div className="flex gap-6 text-xs">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-slate-500 hover:text-amber-400 transition-colors font-medium">Privacy Policy</Link>
            <Link to="/terms-conditions" data-testid="footer-terms-link" className="text-slate-500 hover:text-amber-400 transition-colors font-medium">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
